/**
 * Buying a credit pack — one path, used by every surface that sells one.
 *
 * WHAT THE CLIENT IS TRUSTED WITH: a sku. Nothing else. Not a price, not an
 * amount, not a credit count. The edge function looks the sku up in
 * credit_pack_skus and creates the Checkout session from the Stripe price id on
 * that row, and the grant later reads its credit count from the SAME row. So
 * the money and the credits can never come from two records that disagree.
 *
 * NEVER A SAME-TAB REDIRECT. Locked rule: the dashboard does not navigate the
 * user away. Checkout opens in a new tab; the original tab stays exactly where
 * it was and picks the purchase up when the buyer comes back.
 *
 * TWO INDEPENDENT GRANT PATHS, ONE IDEMPOTENCY. Stripe's webhook is primary.
 * The return path also confirms, because a webhook can be late, and because a
 * purchase must not depend on one arriving at all. Both call
 * public.grant_credit_pack, which claims the Stripe session id against a UNIQUE
 * constraint — so whichever gets there first pays out, and the other is a no-op.
 */
import { supabase } from "@/integrations/supabase/client";
import { adsConversion, adsBeginCheckout } from "@/lib/adsConversion";

export interface PackCheckout {
  url: string;
  sessionId: string;
  sku: string;
  credits: number;
  priceId: string;
  amountTotal: number | null;
}

/**
 * Create the Checkout session and open it in a NEW TAB.
 * Returns the session so the caller can show a pending state immediately.
 */
export async function startPackCheckout(sku: string, returnTo?: string): Promise<PackCheckout> {
  // Opened synchronously, before the await, or the popup blocker eats it — a
  // window.open() after a network round trip is no longer a user gesture.
  const tab = window.open("", "_blank");
  try {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { bundle: "credit_pack", sku, returnTo: returnTo || window.location.pathname },
    });
    if (error) {
      // T425: a refusal (e.g. 403 pack_requires_pro: packs are for Pro and higher) carries its own sentence.
      let msg = error.message;
      try { const b = await (error as any)?.context?.json?.(); if (b?.error) msg = String(b.error); } catch { /* keep the generic one */ }
      throw new Error(msg);
    }
    if (!data?.url) throw new Error(data?.error || "Checkout could not be opened.");
    // Google Ads Begin checkout (T258), before the tab goes to Stripe; resolves within ~1.2 s.
    await adsBeginCheckout();
    if (tab) tab.location.href = data.url;
    else window.open(data.url, "_blank", "noopener");
    // Google Ads checkout_started (cc_tasks row 88). The checkout already opened in
    // its own tab, so this is fire-and-forget.
    void adsConversion("checkout_started", { transaction_id: data.sessionId });
    return data as PackCheckout;
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

export type PackConfirmStatus = "granted" | "pending" | "not_yours" | "error";

export interface PackConfirmation {
  status: PackConfirmStatus;
  credits?: number;
  alreadyGranted?: boolean;
  purchasedBalance?: number;
  sku?: string;
  message?: string;
}

/**
 * Ask the server whether a returned session has actually been paid, and grant
 * if so. The browser only supplies the session id — it is never believed about
 * the payment itself; the server asks Stripe.
 */
export async function confirmPackPurchase(sessionId: string): Promise<PackConfirmation> {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: { action: "confirm_credit_pack", sessionId },
  });
  if (error) return { status: "error", message: error.message };
  if (data?.status === "pending") return { status: "pending" };
  if (data?.status === "not_yours") return { status: "not_yours" };
  if (data?.status === "granted") {
    const g = data.grant || {};
    // Google Ads paid (cc_tasks row 88): the server confirmed the payment with Stripe.
    // De-duplicated per session id, so the retry loop and a refresh count once.
    if (g.ok !== false) void adsConversion("paid", { transaction_id: sessionId });
    return {
      status: "granted",
      credits: Number(g.credits || 0),
      alreadyGranted: g.already_granted === true,
      purchasedBalance: Number(g.purchased_balance || 0),
      sku: g.sku,
      message: g.ok === false ? String(g.reason || "The purchase could not be completed.") : undefined,
    };
  }
  return { status: "error", message: "Unexpected response." };
}

/**
 * Poll for the grant. The webhook and this call race each other by design, and
 * the buyer must never be shown a stale total as if it were final — so the
 * caller shows "payment received, credits landing" until this resolves.
 */
export async function confirmPackWithRetry(
  sessionId: string,
  onUpdate?: (c: PackConfirmation) => void,
  attempts = 6,
): Promise<PackConfirmation> {
  let last: PackConfirmation = { status: "pending" };
  for (let i = 0; i < attempts; i++) {
    last = await confirmPackPurchase(sessionId);
    onUpdate?.(last);
    if (last.status !== "pending") return last;
    await new Promise((r) => setTimeout(r, 1500 + i * 1000));
  }
  return last;
}

/** Read the return parameters Checkout sends back, and clear them from the URL. */
export function readPackReturn(): { status: "success" | "cancel" | null; sessionId: string | null } {
  const q = new URLSearchParams(window.location.search);
  const pack = q.get("pack");
  const sessionId = q.get("session_id");
  if (pack !== "success" && pack !== "cancel") return { status: null, sessionId: null };
  // Clear so a refresh does not re-run the confirmation against a spent session.
  q.delete("pack");
  q.delete("session_id");
  const rest = q.toString();
  window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
  return { status: pack, sessionId };
}
